import './note.less';

import { X as DiscardNoteIcon } from 'react-feather';
import { Save as SaveNoteIcon } from 'react-feather';
import { useEffect }            from 'react';
import { useRef }               from 'react';
import { useState }             from 'react';
import autosize                 from 'autosize';
import CalendarTime             from '../components/calendar_time';
import classnames               from 'classnames';
import EditingNoteIcon          from '../assets/icons/edit_note.svg';
import enums                    from '../util/enums';
import React                    from 'react';


const modes = enums([
  'collapsed',
  'collapsing',
  'expanding',
  'expanded',
  'editing'
]);


export default function Note({ note, team, handleNoteSave, onView, onEdit }) {
  const initialBody       = note?.body ?? '';
  const isNoteEmpty       = !initialBody.trim();
  const [ mode, setMode ] = useState(modes.collapsed);
  const [ body, setBody ] = useState(initialBody);
  const mounted           = useRef();
  const textareaRef       = useRef();

  // If note is empty, enter edit mode immediately. Doing this on a timeout so
  // we can animate from the collapsed state. This effect uses the onEdit prop
  // which changes on every render. We should fix that but that would require
  // some refactoring of the parent components, so not going to do that. We just
  // want this to run on mount.
  useEffect(function() {
    if (isNoteEmpty) {
      const timer = setTimeout(() => {
        switchToEditing();
        focusTextarea();
      }, 10);
      return () => {
        clearTimeout(timer);
      };
    } else
      return () => {};
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function switchToEditing() {
    if (mounted.current) {
      setMode(modes.editing);
      onEdit();
    }
  }

  function switchToExpanded() {
    if (mounted.current) {
      setMode(modes.expanding);
      setTimeout(() => {
        setMode(modes.expanded);
      }, 10);
    }
  }

  function switchToCollapsed() {
    if (mounted.current) {
      setMode(modes.collapsing);
      setTimeout(() => {
        onView();
        setMode(modes.collapsed);
      }, 200); // allow time for collapse animation to complete
    }
  }

  async function save() {
    await handleNoteSave(body);
    switchToCollapsed();
  }

  function cancel() {
    setBody(initialBody);
    switchToCollapsed();
  }

  function focusTextarea() {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.focus();
      textarea.selectionStart = textarea.value.length;
      textarea.selectionEnd   = textarea.value.length;
    }
  }

  const className = classnames('note', mode);

  return (
    <div className={className} ref={mounted}>
      <NoteOverlay {...{ mode, switchToCollapsed }}/>
      <div className="note-container">
        <div className="note-content">
          <NotePreview {...{ note, switchToExpanded, switchToEditing, focusTextarea }}/>
          <NoteEditor {...{ mode, body, setBody, textareaRef, switchToEditing }}/>
          <NoteFooter {...{ note, body, team }}/>
        </div>
        <NoteActions {...{ save, cancel }}/>
      </div>
    </div>
  );
}

function NoteOverlay({ mode, switchToCollapsed }) {
  function onClick() {
    if (mode !== modes.editing)
      switchToCollapsed();
  }
  return (
    <div className="note-overlay" onClick={onClick}></div>
  );
}

function NotePreview({ note, switchToExpanded, switchToEditing, focusTextarea }) {
  function onClickEdit(event) {
    event.stopPropagation();
    switchToEditing();
    setTimeout(focusTextarea, 0);
  }
  return (
    <div className="note-preview" onClick={switchToExpanded} onDoubleClick={onClickEdit}>
      <div className="note-preview-text">{note?.body}</div>
      <div className="edit-icon" onClick={onClickEdit}>
        <EditingNoteIcon/>
      </div>
    </div>
  );
}

export function NoteEditor({ mode, body, setBody, textareaRef, switchToEditing }) {
  const isVisible  = [ modes.expanded, modes.editing ].includes(mode);
  const isReadOnly = [ modes.expanded ].includes(mode);

  function onChange({ target }) {
    const { value } = target;
    if (value || value === '')
      setBody(value.substring(0, 280));
  }

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea && isVisible) {
      autosize(textarea);
      return () => {
        autosize.destroy(textarea);
      };
    } else
      return () => {};
  }, [ isVisible, textareaRef ]);

  return (
    <div className="note-editor">
      <textarea
        ref={textareaRef}
        value={body}
        onChange={onChange}
        onClick={switchToEditing}
        readOnly={isReadOnly}
      />
    </div>
  );
}


function NoteFooter({ note, body, team }) {
  const { date, user } = note;

  return (
    <div className="note-footer">
      <div className="note-edited">
        {date && (
          <>Last edited at {<CalendarTime time={date}/>}</>
        )}
        {date && user && (
          <> by {getUserName()}</>
        )}
      </div>
      <div className="note-length">
        {body?.length ?? 0} / 280
      </div>
    </div>
  );

  function getUserName() {
    const teamUser = team.users.find(u => user === u.id);
    const userName = teamUser?.name;
    return userName;
  }
}


function NoteActions({ save, cancel }) {
  return (
    <div className="note-actions">
      <button className="button cancel" onClick={cancel}>
        <DiscardNoteIcon width="16" height="16"/>
        <span className="label">Cancel</span>
      </button>
      <button className="button button-fill save" onClick={save}>
        <SaveNoteIcon width="16" height="16"/>
        <span className="label">Save</span>
      </button>
    </div>
  );
}
